/**
 * 메모 등 HTML 에디터(Quill) 내용 공통 처리
 * - 예전 메모는 일반 글자, 에디터로 저장한 건 HTML → 둘 다 받아서 보여줌
 * - 화면에 그릴 때는 반드시 sanitizeHtml (DOMPurify) — 스크립트·이벤트 속성 제거
 */
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'span', 'a', 'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3'];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'class', 'style', 'data-list', 'data-checked'];

//HTML 로 저장된 값인지 (태그로 시작)
export function isHtml(v) {
    return typeof v === 'string' && /^\s*<(p|ol|ul|h[1-3]|blockquote|div|span|br|strong|b|i|em|u|s)[\s>/]/i.test(v);
}

function escapeHtml(t) {
    return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

//일반 글자 → 에디터용 HTML (줄마다 <p>)
export function plainToHtml(v) {
    if (!v) return '';
    return String(v).split(/\r?\n/).map(line => '<p>' + (line ? escapeHtml(line) : '<br>') + '</p>').join('');
}

//에디터에 넣을 값: HTML 이면 그대로(정리), 일반 글자면 변환
export function toEditorHtml(v) {
    if (!v) return '';
    return isHtml(v) ? sanitizeHtml(v) : plainToHtml(v);
}

//화면 표시용 안전한 HTML
export function sanitizeHtml(v) {
    if (!v) return '';
    const html = isHtml(v) ? v : plainToHtml(v);
    const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOW_DATA_ATTR: false });
    //링크는 새 창으로
    return clean.replace(/<a\s/gi, '<a target="_blank" rel="noopener noreferrer" ');
}

//목록 미리보기·예전 화면 입력칸용 글자만 (줄바꿈 유지)
export function htmlToPlain(v) {
    if (!v) return '';
    if (!isHtml(v)) return String(v);
    const div = document.createElement('div');
    div.innerHTML = DOMPurify.sanitize(String(v).replace(/<\/(p|li|h[1-3]|blockquote)>/gi, '\n').replace(/<br\s*\/?>/gi, '\n'), { ALLOWED_TAGS: [] });
    return (div.textContent || '').replace(/\n{3,}/g, '\n\n').replace(/\n+$/, '');
}

//에디터 빈 값 (<p><br></p> 등)
export function isEmptyHtml(v) {
    return !htmlToPlain(v).trim();
}

//저장할 값: 비었으면 '', 아니면 HTML
export function normalizeEditorHtml(v) {
    return isEmptyHtml(v) ? '' : v;
}

//두 메모가 같은 내용인지 (예전 일반 글자 ↔ 에디터가 바꾼 HTML 도 같게 봄) — '저장 안 됨' 판단용
export function canonicalRichText(v) {
    if (isEmptyHtml(v)) return '';
    const div = document.createElement('div');
    div.innerHTML = sanitizeHtml(v);
    return div.innerHTML;
}
export function sameRichText(a, b) {
    return canonicalRichText(a || '') === canonicalRichText(b || '');
}
